import React, { PropTypes } from "react";
import { FormattedHTMLMessage, FormattedMessage, intlShape } from "react-intl";
import { connect } from "react-redux";
import { Link } from "react-router";
import { bindActionCreators } from "redux";
import { change as changeFieldValue, Field, formValueSelector, reduxForm } from "redux-form";
import {
  CheckboxInput,
  IntlFieldGroup,
  SelectInput,
  TextInput,
} from "../../../common/form";

import guaranteeCategoryTypes from "./enums/guaranteeCategoryTypes";
import guaranteeTypes from "./enums/guaranteeTypes";


import simpleFetch from "../../../../../utils/simpleFetch";
import validationUtils from "../../../../utils/ValidationUtils";
import { CLIENT_GUARANTEE_CONFIGURATIONS_BY_GUARANTEE_ID, GUARANTEES } from "../../../../utils/data/models";
import BSModal from "../../../common/BSModal";
import InfoTooltip from "../../../common/Tooltip/InfoTooltip";
import { FR } from "../../../common/enums/languages";
import { GUARANTEE_VALIDATION_RULES } from "./guaranteeValidationRules";
import { HIGH_MANAGEMENT } from "../../../../utils/Roles";
import userHasAccess from "../../../../utils/userHasAccess";

class BasicGuaranteeFields extends React.Component {
  static generateLocalizedRules(model) {
    let newRules = { ...GUARANTEE_VALIDATION_RULES };

    GUARANTEES.localizedFields.map((field) => {
      Object.keys(model.localizations).map((locale) => {
        const fieldName = `localizations['${locale}'].${field}`;

        newRules[fieldName] = newRules[field];
      });
    });
    GUARANTEES.localizedFields.map((field) => {
      delete newRules[field];
    });

    return newRules;
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      showFormula: props.initialValues.guaranteeType === "FORMULA",
      masterGuarantees: [],
      showClientConfigModal: false,
      blockingClients: [],
    };

    this.changeType = this.changeType.bind(this);
    this.generateLocalizedFields = this.generateLocalizedFields.bind(this);
    this.loadMasterGuarantees = this.loadMasterGuarantees.bind(this);
    this.checkForConfigurationsInClients = this.checkForConfigurationsInClients.bind(this);
    this.closeClientConfigModal = this.closeClientConfigModal.bind(this);
  }

  getChildContext() {
    return {
      scene: this.props.scene,
    };
  }

  componentWillMount() {
    this.loadMasterGuarantees();
  }

  loadMasterGuarantees() {
    simpleFetch("GET", GUARANTEES.path, {
      query: {
        filters: "guarantees.isMaster==true",
        fields: "guaranteeId,localizations",
        limit: 1000,
      },
    })
      .then((data) => {
        const currentGuaranteeId =
          this.props.initialValues && this.props.initialValues.guaranteeId;
        const items = (data.items || data || []).filter(
          (g) => g.guaranteeId !== currentGuaranteeId
        );
        const lang = this.context.intl.locale || FR.value;

        const options = [{ value: "", text: " " }].concat(
          items.map((g) => ({
            value: String(g.guaranteeId),
            text:
              (g.localizations &&
                g.localizations[lang] &&
                g.localizations[lang].name) ||
              String(g.guaranteeId),
          }))
        );
        this.setState({ masterGuarantees: options });
      })
      .catch((e) => {
        this.setState({ masterGuarantees: [{ value: "", text: " " }] });
      });
  }

  checkForConfigurationsInClients(checked) {
    if (!checked) {
      this.setState({ showClientConfigModal: false });
      return;
    }
    const currentGuaranteeId =
      this.props.initialValues && this.props.initialValues.guaranteeId;
    currentGuaranteeId && simpleFetch("GET", CLIENT_GUARANTEE_CONFIGURATIONS_BY_GUARANTEE_ID.path.replace("{}", currentGuaranteeId), {})
      .then((data) => {
        const blockingClients = data.map((config) => (config.client) || {});
        blockingClients.length > 0 && this.setState({
          showClientConfigModal: true,
          blockingClients: blockingClients,
        });
      })
  }

  closeClientConfigModal() {
    this.props.changeFieldValue(
      "guarantee",
      "isDrivingMandatoryDependentGuarantee",
      false
    );
    this.setState({ showClientConfigModal: false });
  }

  generateLocalizedFields() {
    return GUARANTEES.localizedFields.map((field) => {
      return (
        <IntlFieldGroup
          key={field}
          field={field}
          required={field == "name"}
          labelKey={`admin.guarantees.form.${field}`}
          localizations={this.props.initialValues.localizations}
        />
      );
    });
  }

  renderClientConfigModal() {
    const uniqueClientsMap = {};
    this.state.blockingClients.forEach((client) => {
      if (client.fullName && client.clientId) {
        uniqueClientsMap[client.clientId] = client;
      }
    });
    const uniqueClients = Object.values(uniqueClientsMap);
    const hasHighManagementAccess = userHasAccess(HIGH_MANAGEMENT);
    const clientLinks = uniqueClients.map((client) => (
      hasHighManagementAccess ? (
        <a
          key={client.clientId}
          href={`/dashboard/clients/${client.clientId}/client-guarantees`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {client.fullName}
        </a>
      ) : (
        <span key={client.clientId}>{client.fullName}</span>
      )
    ));
    const modalMessageID = uniqueClients.length > 1 ? "clientConfigurationMessagePlural" : "clientConfigurationMessageSingular"
    return (
      <BSModal
        show={this.state.showClientConfigModal}
        onClose={this.closeClientConfigModal}
        title={
          <span>
            <FormattedMessage id="validation.clientFieldValidation.attention">
              {(f) => <span className="icon warning">{f}</span>}
            </FormattedMessage>
          </span>
        }
        backdrop={false}
      >
        <FormattedHTMLMessage
          id={`admin.guarantees.modal.${modalMessageID}`}
        />
        <ul>
          {clientLinks.map((link, index) => (
            <li key={index}>
              {link}
            </li>
          ))}
        </ul>
      </BSModal>
    );
  }

  changeType(value) {
    this.setState({ ...this.state, showFormula: value === "FORMULA" });
    return value;
  }

  isDependsOnDisabled() {
    // A master guarantee cannot depend on another guarantee
    if (this.props.initialValues && this.props.initialValues.isMaster) {
      return true;
    }
    return !!this.props.isDrivingMandatoryDependentChecked;
  }

  isDrivingDisabledByDependency() {
    return !!this.props.dependsOnGuaranteeId;
  }

  render() {
    const { quebecChecked, outOfQuebecChecked, isMandatoryDependentChecked, isDrivingMandatoryDependentChecked } = this.props;
    const dependsOnDisabled = this.isDependsOnDisabled();
    const drivingDisabledByDependency = this.isDrivingDisabledByDependency();
    const { formatMessage } = this.context.intl;

    return (
      <div>
        {this.renderClientConfigModal()}
        <form onSubmit={this.props.handleSubmit}>
          {this.generateLocalizedFields()}

          <Field
            id="additionalCode"
            name="additionalCode"
            component={TextInput}
            type="text"
            label={<FormattedMessage id="admin.guarantees.form.code" />}
          />

          <div className="two-columns-section">
            <Field
              id="isTaxableBenefitsFederal"
              name="isTaxableBenefitsFederal"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              label={
                <FormattedMessage id="admin.guarantees.form.taxableIncomeFederal" />
              }
              normalize={(value) => !!value}
            />
            <Field
              id="isQuebecHealthPremium"
              name="isQuebecHealthPremium"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              disabled={outOfQuebecChecked}
              label={
                <FormattedMessage id="admin.guarantees.form.quebecHealthPremium" />
              }
              normalize={(value) => !!value}
            />
            <Field
              id="isTaxableBenefitsQuebec"
              name="isTaxableBenefitsQuebec"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              label={
                <FormattedMessage id="admin.guarantees.form.taxableIncomeQuebec" />
              }
              normalize={(value) => !!value}
            />
            <Field
              id="isTaxableBenefitsProvincial"
              name="isTaxableBenefitsProvincial"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              disabled={quebecChecked}
              label={
                <FormattedMessage id="admin.guarantees.form.provincialTaxableBenefits" />
              }
              normalize={(value) => !!value}
            />
          </div>

          <Field
            id="guaranteeType"
            name="guaranteeType"
            component={SelectInput}
            options={guaranteeTypes}
            label={<FormattedMessage id="admin.guarantees.form.type" />}
            normalize={this.changeType}
            disabled={!this.props.add}
          />

          {this.state.showFormula && (
            <Field
              id="formulaDenominator"
              name="formulaDenominator"
              component={SelectInput}
              options={[
                { value: "1", text: "1" },
                { value: "10", text: "10" },
                { value: "100", text: "100" },
                { value: "1000", text: "1000" },
              ]}
              label={<FormattedMessage id="admin.guarantees.form.formula" />}
            />
          )}

          <Field
            id="category"
            name="category"
            component={SelectInput}
            options={guaranteeCategoryTypes}
            label={<FormattedMessage id="admin.guarantees.form.guaranteeCategory" />}
            required={true}
          />

          <Field
            id="dependsOnGuaranteeId"
            name="dependsOnGuaranteeId"
            component={SelectInput}
            addBlankOption={true}
            options={this.state.masterGuarantees}
            disabled={dependsOnDisabled}
            label={
              <span>
                <FormattedMessage id="admin.guarantees.form.dependsOnGuaranteeId" />
                <InfoTooltip
                  id="depends-on-guarantee-tooltip"
                  text={formatMessage({ id: "admin.guarantees.form.dependsOnGuaranteeIdTooltip" })}
                />
              </span>
            }
            normalize={(value) => (value === "" || value == null ? null : Number(value))}
          />

          <div className="is-guarantee-checked">
            <Field
              id="isAutomaticallyChecked"
              name="isAutomaticallyChecked"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              label={
                <FormattedMessage id="admin.guarantees.form.isAutomaticallyChecked" />
              }
              normalize={(value) => !!value}
            />
            <InfoTooltip
              id="automatically-checked-tooltip"
              text={formatMessage({ id: "admin.guarantees.form.isAutomaticallyCheckedTooltip" })}
            />
          </div>

          <div className="is-guarantee-checked">
            <Field
              id="isMandatoryDependentGuarantee"
              name="isMandatoryDependentGuarantee"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              disabled={isDrivingMandatoryDependentChecked}
              label={
                <FormattedMessage id="admin.guarantees.form.isMandatoryDependentGuarantee" />
              }
              normalize={(value) => !!value}
            />
            <InfoTooltip
              id="mandatory-dependent-guarantee-tooltip"
              text={formatMessage({ id: "admin.guarantees.form.isMandatoryDependentGuaranteeTooltip" })}
            />
          </div>
          <div className="is-guarantee-checked">
            <Field
              id="isDrivingMandatoryDependentGuarantee"
              name="isDrivingMandatoryDependentGuarantee"
              component={CheckboxInput}
              groupClassName="checkbox-first"
              checkbox={{ value: true }}
              props={{ onChange: this.checkForConfigurationsInClients }}
              disabled={isMandatoryDependentChecked || drivingDisabledByDependency}
              label={
                <FormattedMessage id="admin.guarantees.form.isDrivingMandatoryDependentGuarantee" />
              }
              normalize={(value) => !!value}
            />
            <InfoTooltip
              id="driving-mandatory-dependent-guarantee-tooltip"
              text={formatMessage({ id: "admin.guarantees.form.isDrivingMandatoryDependentGuaranteeTooltip" })}
            />
          </div>
        </form>
      </div>
    );
  }
}

BasicGuaranteeFields.propTypes = {
  add: PropTypes.bool,
  initialValues: PropTypes.object,
  scene: PropTypes.string,
  validation: PropTypes.object,
  handleSubmit: PropTypes.func,
  quebecChecked: PropTypes.bool,
  outOfQuebecChecked: PropTypes.bool,
  guaranteeType: PropTypes.string,
  isMandatoryDependentChecked: PropTypes.bool,
  isDrivingMandatoryDependentChecked: PropTypes.bool,
  dependsOnGuaranteeId: PropTypes.number,
};

BasicGuaranteeFields.defaultProps = {
  scene: "",
};

BasicGuaranteeFields.contextTypes = {
  getData: PropTypes.func,
  intl: intlShape,
};

BasicGuaranteeFields.childContextTypes = {
  scene: PropTypes.string,
};

const selector = formValueSelector("guarantee");

const mapStateToProps = (state) => ({
  quebecChecked: !!selector(state, "isQuebecHealthPremium"),
  outOfQuebecChecked: !!selector(state, "isTaxableBenefitsProvincial"),
  guaranteeType: selector(state, "guaranteeType"),
  isMandatoryDependentChecked: !!selector(state, "isMandatoryDependentGuarantee"),
  isDrivingMandatoryDependentChecked: !!selector(state, "isDrivingMandatoryDependentGuarantee"),
  dependsOnGuaranteeId: selector(state, "dependsOnGuaranteeId"),
});

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(
  reduxForm({
    form: "guarantee",
    validate: (values) =>
      validationUtils.validate(
        values,
        BasicGuaranteeFields.generateLocalizedRules(values)
      ),
  })(BasicGuaranteeFields)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/admin/guarantees/BasicGuaranteeFields.js